import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import { extractApiErrorMessage } from '../../../utils/apiError'
import type { RootState } from '../../store'
import type {
  AvailableEmployeeRecord,
  AvailableEmployeeSearchParams,
} from './approverProxySlice'

// Re-export shared types so consumers import from a single location.
export type { AvailableEmployeeRecord } from './approverProxySlice'
export type { AvailableEmployeeSearchParams } from './approverProxySlice'

// ============================================================================
// TYPES
// ============================================================================

/**
 * Search mode options accepted by the float-employee search API.
 * These map 1:1 to the API `searchOption` query param.
 */
export type FloatEmployeeSearchOption = 'name' | 'nuid' | 'approvalGrpId'

/**
 * Payload shape for the float employee search thunk.
 * - `searchOption='name'`          → provide `searchLName` (required, min 2) and optional `searchFName`
 * - `searchOption='nuid'`          → provide `searchValue` (the NUID)
 * - `searchOption='approvalGrpId'` → provide `searchValue` (the Approval Group ID)
 */
export interface FloatEmployeeSearchParams {
  /** Which field the search is performed against. */
  searchOption: FloatEmployeeSearchOption
  /** The NUID or Approval Group ID; used when searchOption is 'nuid' or 'approvalGrpId'. */
  searchValue?: string
  /** First name; optional and only meaningful when searchOption is 'name'. */
  searchFName?: string
  /** Last name; required (min length 2) when searchOption is 'name'. */
  searchLName?: string
}

/**
 * Shape of a single record returned by the float-employee search API.
 * Fields match the actual API response keys (`GET /tort/float-employee/searchFE`).
 */
export interface FloatEmployeeSearchRecord {
  /** Approval group identifier, e.g. "00007227". */
  approvalGrpId: string
  /** Geographic region display name, e.g. "Northern California". */
  region: string
  /** Region code for assignment operations, e.g. "NCA", "SCA". */
  regionCode: string
  /** Department name and code. */
  department: string
  /** Primary approver name and NUID, e.g. "SMITH, JOHN M (K817163)". */
  primaryApprover: string
  /** Float employees joined by "; ". Empty string when none are assigned. */
  floatEmployee: string
  [key: string]: unknown
}

/**
 * Shape of a single record returned by the fetchCurrentFE API
 * (`GET /tort/float-employee/fetchCurrentFE`). Represents one currently
 * active float-employee assignment for an approval group.
 */
export interface CurrentFloatEmployeeRecord {
  /** Full float employee name and NUID, e.g. "DOE, JANE A (K123456)". */
  floatEmployee: string
  /** Geographic region, e.g. "Northern California". */
  region: string
  /** Department name, or null when unavailable. */
  department: string | null
  /** Assignment start date in Oracle DD-MON-YY format, e.g. "01-JAN-26", or null. */
  startDate: string | null
  /** Assignment end date in Oracle DD-MON-YY format, e.g. "31-DEC-26", or null. */
  endDate: string | null
  /** Float employee email address, or null when unavailable. */
  email: string | null
}

/**
 * A single float-employee assignment entry included in the save payload.
 */
export interface AssignFloatEmployeeEntry {
  /** Full display name of the selected float employee, e.g. "BROWN, ROBERT (K789012)". */
  selectedFEmployeeName: string
  /** Assignment start date in MM/DD/YYYY format. */
  fromDate: string
  /** Assignment end date in MM/DD/YYYY format. */
  toDate: string
  /** Department name for the assignment. */
  department: string
  /** Geographic region for the assignment. */
  region: string
  /** Float employee email address. */
  email: string
}

/**
 * Payload shape for the {@link assignFloatEmployees} thunk.
 */
export interface AssignFloatEmployeesPayload {
  /** Approval group identifier to update. */
  approvalGrpId: string
  /** Region code for the approval group. */
  regionCode: string
  /** Full name of the primary approver for the group. */
  primaryApproverName: string
  /** True when all float employees are being removed (saving an empty list). */
  saveNone: boolean
  /** The updated list of float-employee assignments after the user's edits. */
  currentFloatEmployees: AssignFloatEmployeeEntry[]
}

/** State shape for the floatEmployee slice. */
export interface FloatEmployeeState {
  /** True while the search API call is in-flight. */
  isLoading: boolean
  /** True once the user has submitted at least one search — controls empty-state visibility. */
  searchPerformed: boolean
  /** Search results returned by the API. Empty array when no matches. */
  data: FloatEmployeeSearchRecord[]
  /** Human-readable error message when the API call fails; null otherwise. */
  error: string | null
  /** True while the fetchCurrentFE API call is in-flight. */
  isLoadingFetchCurrent: boolean
  /** Current float-employee records fetched on Edit click. */
  currentFloatEmployees: CurrentFloatEmployeeRecord[]
  /** Error from the fetchCurrentFE call, or null. */
  fetchCurrentError: string | null
  /** True while the searchAvailableEmployees API call is in-flight. */
  isLoadingEmployeeSearch: boolean
  /** True once an employee search has been submitted — controls empty-state visibility. */
  employeeSearchPerformed: boolean
  /** Eligible employees returned by the most recent available-employee search. */
  employeeResults: AvailableEmployeeRecord[]
  /** Error from the searchAvailableEmployees call, or null. */
  employeeSearchError: string | null
  /** True while the assignFloatEmployees API call is in-flight. */
  isLoadingSave: boolean
  /** Error from the most recent failed assignFloatEmployees call, or null. */
  saveError: string | null
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: FloatEmployeeState = {
  isLoading: false,
  searchPerformed: false,
  data: [],
  error: null,
  isLoadingFetchCurrent: false,
  currentFloatEmployees: [],
  fetchCurrentError: null,
  isLoadingEmployeeSearch: false,
  employeeSearchPerformed: false,
  employeeResults: [],
  employeeSearchError: null,
  isLoadingSave: false,
  saveError: null,
}

// ============================================================================
// ASYNC THUNKS
// ============================================================================

/**
 * Searches for float-employee records via the BFF search endpoint.
 *
 * GET /tort/float-employee/searchFE
 *
 * Query params built from the payload:
 * - Always:          actionCode=searchFE, searchOption=<mode>
 * - name:            searchLName=<last> (required, min 2), searchFName=<first> (optional)
 * - nuid|group:      searchValue=<input>
 *
 * The `x-nuid` identity header is attached by the shared Axios interceptor.
 *
 * @param payload - Search option and corresponding field values.
 * @returns Array of matching {@link FloatEmployeeSearchRecord} objects (empty when none).
 */
export const searchFloatEmployees = createAsyncThunk<
  FloatEmployeeSearchRecord[],
  FloatEmployeeSearchParams
>(
  'admin/floatEmployee/search',
  async (payload, { rejectWithValue, signal }) => {
    try {
      const params: Record<string, string> = {
        actionCode: 'searchFE',
        searchOption: payload.searchOption,
      }

      if (payload.searchOption === 'name') {
        if (payload.searchLName) params.searchLName = payload.searchLName
        if (payload.searchFName) params.searchFName = payload.searchFName
      } else if (payload.searchValue) {
        params.searchValue = payload.searchValue
      }

      const response = await api.get<FloatEmployeeSearchRecord[]>(
        endpoints.admin.searchFloatEmployees,
        { params, signal }
      )
      return response.data
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to search float employees')
      )
    }
  }
)

/**
 * Fetches the currently active float-employee assignments for an approval group.
 *
 * GET /tort/float-employee/fetchCurrentFE
 *
 * Query params:
 * - `actionCode=fetchCurrentFE` (constant)
 * - `timeStamp=<epoch ms>` — client sysdate sent as a string for server-side auditing
 * - `approvalGrpId=<id>` — the target approval group
 *
 * The `x-nuid` identity header is attached by the shared Axios interceptor.
 *
 * @param approvalGrpId - The approval group ID to fetch current float employees for.
 * @returns Array of {@link CurrentFloatEmployeeRecord} objects (empty when none).
 */
export const fetchCurrentFloatEmployees = createAsyncThunk<
  CurrentFloatEmployeeRecord[],
  string
>(
  'admin/floatEmployee/fetchCurrent',
  async (approvalGrpId, { rejectWithValue, signal }) => {
    try {
      const response = await api.get<CurrentFloatEmployeeRecord[]>(
        endpoints.admin.fetchCurrentFloatEmployees,
        {
          params: {
            actionCode: 'fetchCurrentFE',
            timeStamp: Date.now().toString(),
            approvalGrpId,
          },
          signal,
        }
      )
      return response.data
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to fetch current float employees')
      )
    }
  }
)

/**
 * Searches for employees eligible to be added as a float employee for a group.
 *
 * GET /tort/float-employee/searchAvailableEmployees
 *
 * The API excludes employees who are already the primary approver or an existing
 * float employee for the group. Query params built from the payload:
 * - Always: `actionCode=empFESearch`, `approvalGrpId=<digits>`, `searchOption=<mode>`
 * - name:   `searchLName=<last>` (required, min 2), `searchFName=<first>` (optional)
 * - nuid:   `searchValue=<nuid>`
 *
 * The `x-nuid` identity header is attached by the shared Axios interceptor.
 *
 * @param payload - Approval group, search option, and corresponding field values.
 * @returns Array of matching {@link AvailableEmployeeRecord} objects (empty when none).
 */
export const searchAvailableFloatEmployees = createAsyncThunk<
  AvailableEmployeeRecord[],
  AvailableEmployeeSearchParams
>(
  'admin/floatEmployee/searchAvailable',
  async (payload, { rejectWithValue, signal }) => {
    try {
      const params: Record<string, string> = {
        actionCode: 'empFESearch',
        approvalGrpId: payload.approvalGrpId,
        searchOption: payload.searchOption,
      }

      if (payload.searchOption === 'name') {
        if (payload.searchLName) params.searchLName = payload.searchLName
        if (payload.searchFName) params.searchFName = payload.searchFName
      } else if (payload.searchValue) {
        params.searchValue = payload.searchValue
      }

      const response = await api.get<AvailableEmployeeRecord[]>(
        endpoints.admin.searchAvailableFloatEmployees,
        { params, signal }
      )
      return response.data
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to search available float employees')
      )
    }
  }
)

/**
 * Saves the current float-employee assignments for an approval group.
 *
 * POST /tort/float-employee/assignFloatEmployees
 *
 * @param payload - The approval group details and updated float-employee list.
 * @returns void on success.
 */
export const assignFloatEmployees = createAsyncThunk<
  void,
  AssignFloatEmployeesPayload
>(
  'admin/floatEmployee/assign',
  async (payload, { rejectWithValue }) => {
    try {
      await api.post(endpoints.admin.assignFloatEmployees, {
        actionCode: 'assignFE',
        approvalGrpId: payload.approvalGrpId,
        regionCode: payload.regionCode,
        primaryApproverName: payload.primaryApproverName,
        saveNone: payload.saveNone,
        currentFloatEmployees: payload.currentFloatEmployees,
      })
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to save float employee assignments')
      )
    }
  }
)

// ============================================================================
// SLICE
// ============================================================================

const floatEmployeeSlice = createSlice({
  name: 'floatEmployee',
  initialState,
  reducers: {
    /**
     * Resets the slice to its initial state.
     * Call this when navigating away from the float-employee page to avoid
     * stale results appearing on re-entry.
     */
    clearFloatEmployeeState: (state) => {
      state.isLoading = false
      state.searchPerformed = false
      state.data = []
      state.error = null
      state.isLoadingFetchCurrent = false
      state.currentFloatEmployees = []
      state.fetchCurrentError = null
      state.isLoadingEmployeeSearch = false
      state.employeeSearchPerformed = false
      state.employeeResults = []
      state.employeeSearchError = null
      state.isLoadingSave = false
      state.saveError = null
    },
  },
  extraReducers: (builder) => {
    // ── searchFloatEmployees ────────────────────────────────────────────────
    builder.addCase(searchFloatEmployees.pending, (state) => {
      state.isLoading = true
      state.searchPerformed = true
      state.error = null
      state.data = []
      // Clear stale edit-panel errors and employee search results so they do
      // not persist across searches.
      state.saveError = null
      state.employeeSearchError = null
      state.employeeResults = []
      state.employeeSearchPerformed = false
    })
    builder.addCase(searchFloatEmployees.fulfilled, (state, action) => {
      state.isLoading = false
      state.data = action.payload
    })
    builder.addCase(searchFloatEmployees.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload as string
    })
    // ── fetchCurrentFloatEmployees ──────────────────────────────────────────
    builder.addCase(fetchCurrentFloatEmployees.pending, (state) => {
      state.isLoadingFetchCurrent = true
      state.fetchCurrentError = null
      state.currentFloatEmployees = []
    })
    builder.addCase(fetchCurrentFloatEmployees.fulfilled, (state, action) => {
      state.isLoadingFetchCurrent = false
      state.currentFloatEmployees = action.payload
    })
    builder.addCase(fetchCurrentFloatEmployees.rejected, (state, action) => {
      state.isLoadingFetchCurrent = false
      state.fetchCurrentError = action.payload as string
    })
    // ── searchAvailableFloatEmployees ───────────────────────────────────────
    builder.addCase(searchAvailableFloatEmployees.pending, (state) => {
      state.isLoadingEmployeeSearch = true
      state.employeeSearchPerformed = true
      state.employeeSearchError = null
      state.employeeResults = []
    })
    builder.addCase(searchAvailableFloatEmployees.fulfilled, (state, action) => {
      state.isLoadingEmployeeSearch = false
      state.employeeResults = action.payload
    })
    builder.addCase(searchAvailableFloatEmployees.rejected, (state, action) => {
      state.isLoadingEmployeeSearch = false
      state.employeeSearchError = action.payload as string
    })
    // ── assignFloatEmployees ────────────────────────────────────────────────
    builder.addCase(assignFloatEmployees.pending, (state) => {
      state.isLoadingSave = true
      state.saveError = null
    })
    builder.addCase(assignFloatEmployees.fulfilled, (state) => {
      state.isLoadingSave = false
    })
    builder.addCase(assignFloatEmployees.rejected, (state, action) => {
      state.isLoadingSave = false
      state.saveError = action.payload as string
    })
  },
})

export const { clearFloatEmployeeState } = floatEmployeeSlice.actions

// ============================================================================
// SELECTORS
// ============================================================================

/** True while the float employee search API call is in-flight. */
export const selectFloatEmployeeLoading = (state: RootState): boolean =>
  state.floatEmployee.isLoading

/** True once the user has performed at least one float employee search. */
export const selectFloatEmployeeSearchPerformed = (state: RootState): boolean =>
  state.floatEmployee.searchPerformed

/** Search results from the most recent float employee search. */
export const selectFloatEmployeeData = (
  state: RootState
): FloatEmployeeSearchRecord[] => state.floatEmployee.data

/** Error message from the most recent failed float employee search, or null. */
export const selectFloatEmployeeError = (state: RootState): string | null =>
  state.floatEmployee.error

/** True while the fetchCurrentFE API call is in-flight. */
export const selectFloatFetchCurrentLoading = (state: RootState): boolean =>
  state.floatEmployee.isLoadingFetchCurrent

/** Current float-employee records fetched by the most recent fetchCurrentFE call. */
export const selectFloatCurrentEmployees = (
  state: RootState
): CurrentFloatEmployeeRecord[] => state.floatEmployee.currentFloatEmployees

/** Error message from the most recent failed fetchCurrentFE call, or null. */
export const selectFloatFetchCurrentError = (state: RootState): string | null =>
  state.floatEmployee.fetchCurrentError

/** True while the searchAvailableEmployees (FE) API call is in-flight. */
export const selectFEAvailableEmployeeLoading = (state: RootState): boolean =>
  state.floatEmployee.isLoadingEmployeeSearch

/** True once the user has performed at least one available-employee search for float. */
export const selectFEAvailableEmployeeSearchPerformed = (
  state: RootState
): boolean => state.floatEmployee.employeeSearchPerformed

/** Eligible employees returned by the most recent float available-employee search. */
export const selectFEAvailableEmployeeResults = (
  state: RootState
): AvailableEmployeeRecord[] => state.floatEmployee.employeeResults

/** Error message from the most recent failed float available-employee search, or null. */
export const selectFEAvailableEmployeeError = (
  state: RootState
): string | null => state.floatEmployee.employeeSearchError

/** True while the assignFloatEmployees API call is in-flight. */
export const selectFESaveLoading = (state: RootState): boolean =>
  state.floatEmployee.isLoadingSave

/** Error from the most recent failed assignFloatEmployees call, or null. */
export const selectFESaveError = (state: RootState): string | null =>
  state.floatEmployee.saveError

export default floatEmployeeSlice.reducer
